// tableBookingApi.js - API utilities for Table Booking operations
import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

// AppSync GraphQL endpoint (to be configured)
// This will be set up when AppSync is configured
const APPSYNC_GRAPHQL_ENDPOINT = process.env.REACT_APP_APPSYNC_GRAPHQL_ENDPOINT || '';

/**
 * Function to get the authentication token.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      return tokens.idToken.toString();
    } else {
      console.error('No ID Token found in the current session.');
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Table Booking API: Making ${method} request to: ${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `Table Booking API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${errorData.error || errorData.message || 'Unknown error'}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_table_booking',
        `Table Booking API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[tableBookingApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(`Table Booking API: Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_table_booking',
      `Table Booking API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * GraphQL helper for AppSync queries/mutations
 */
const makeGraphQLRequest = async (query, variables = {}) => {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication token is missing.');
  }

  if (!APPSYNC_GRAPHQL_ENDPOINT) {
    console.warn('AppSync GraphQL endpoint not configured. Using REST fallback.');
    // Fallback to REST if AppSync not configured
    return null;
  }

  try {
    const response = await fetch(APPSYNC_GRAPHQL_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        query,
        variables,
      }),
    });

    if (!response.ok) {
      throw new Error(`GraphQL request failed: ${response.statusText}`);
    }

    const data = await response.json();
    if (data.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
    }

    return data.data;
  } catch (error) {
    console.error('GraphQL request error:', error);
    throw error;
  }
};

/**
 * API utility functions for Table Booking Management.
 */
export const tableBookingApi = {
  // ==================== Floor Plans (DEPRECATED) ====================
  // Note: Floorplans are deprecated. Use floor_name and floor_no directly on Table endpoints.
  
  /**
   * Get all floor plans for a site
   * GET /tables/floorplans?site_id={siteId}
   * @deprecated Use floor_name and floor_no directly on Table endpoints
   */
  getFloorPlans: async (siteId) => {
    const response = await makeApiRequest(`/tables/floorplans?site_id=${siteId}`);
    return response.json();
  },

  /**
   * Get a specific floor plan
   * GET /tables/floorplans/{floorplanId}
   * @deprecated Use floor_name and floor_no directly on Table endpoints
   */
  getFloorPlan: async (floorplanId) => {
    const response = await makeApiRequest(`/tables/floorplans/${floorplanId}`);
    return response.json();
  },

  /**
   * Create a new floor plan
   * POST /tables/floorplans
   * @deprecated Use floor_name and floor_no directly on Table endpoints
   */
  createFloorPlan: async (floorPlanData) => {
    const response = await makeApiRequest('/tables/floorplans', 'POST', floorPlanData);
    return response.json();
  },

  /**
   * Update a floor plan
   * PUT /tables/floorplans/{floorplanId}
   * @deprecated Use floor_name and floor_no directly on Table endpoints
   */
  updateFloorPlan: async (floorplanId, floorPlanData) => {
    const response = await makeApiRequest(`/tables/floorplans/${floorplanId}`, 'PUT', floorPlanData);
    return response.json();
  },

  /**
   * Delete a floor plan
   * DELETE /tables/floorplans/{floorplanId}
   * @deprecated Use floor_name and floor_no directly on Table endpoints
   */
  deleteFloorPlan: async (floorplanId) => {
    const response = await makeApiRequest(`/tables/floorplans/${floorplanId}`, 'DELETE');
    return response.json();
  },

  // ==================== Tables ====================

  /**
   * Get all tables for a floor plan (legacy - deprecated)
   * GET /tables/tables?floorplan_id={floorplanId}
   * @deprecated Use getTablesWithFilters with site_id instead
   */
  getTables: async (floorplanId) => {
    const response = await makeApiRequest(`/tables/tables?floorplan_id=${floorplanId}`);
    return response.json();
  },

  /**
   * Get a specific table (legacy - may not be supported)
   * Note: Use getTablesWithFilters and filter by table_id instead
   */
  getTable: async (tableId) => {
    // This endpoint may not exist in new API, using tables list with filter
    const response = await makeApiRequest(`/tables/tables?table_id=${tableId}`);
    return response.json();
  },

  /**
   * Create table(s) - Single or Bulk
   * POST /tables/tables
   * Supports both single table creation and bulk creation
   * @param {Object} tableData - Single table: { site_id, table_no, floor_name, floor_no, section, capacity, table_label, merge_group, floorplan_id? }
   *                            - Bulk: { site_id, floor_name, floor_no, section, tables: [{ table_no, capacity, table_label, floor_no?, floor_name? }] }
   */
  createTables: async (tableData) => {
    const response = await makeApiRequest('/tables/tables', 'POST', tableData);
    const data = await response.json();
    
    // Log API event for successful creation
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully created table(s)`,
      {
        endpoint: '/tables/tables',
        method: 'POST',
        siteId: tableData.site_id,
        isBulk: !!tableData.tables,
        tablesCount: tableData.tables?.length || 1,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /**
   * Get tables with filters
   * GET /tables/tables?site_id={site_id}&section={section}&floor_no={int}&floor_name={floor_name}&floorplan_id={floorplan_id}
   * @param {Object} filters - Query parameters: { site_id (required), section?, floor_no?, floor_name?, floorplan_id? }
   * @returns {Promise<Object>} Response with tables array and count: { tables: [...], count: number }
   */
  getTablesWithFilters: async (filters = {}) => {
    if (!filters.site_id) {
      throw new Error('site_id is required');
    }

    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    const endpoint = `/tables/tables?${queryString}`;
    const response = await makeApiRequest(endpoint);
    const data = await response.json();
    
    // Log API event for successful fetch
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully fetched tables with filters`,
      {
        endpoint: '/tables/tables',
        method: 'GET',
        siteId: filters.site_id,
        count: data.tables?.length || data.count || 0,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /**
   * Update a table
   * PUT /tables/tables?site_id={site_id}&table_id={table_id}
   * OR PUT /tables/tables?site_id={site_id}&table_no={table_no}&floor_no={floor_no}
   * @param {string} siteId - Site identifier (required)
   * @param {string|number} tableIdentifier - Table ID (tbl_uuid) OR table number
   * @param {number} floorNo - Floor number (required if using table_no instead of table_id)
   * @param {Object} updateData - Partial update data: { capacity?, section?, floor_name?, floor_no?, table_label?, table_no? }
   * @returns {Promise<Object>} Response with updated table: { message: string, table: {...} }
   */
  updateTableBySiteAndNumber: async (siteId, tableIdentifier, updateData = {}, floorNo = null) => {
    if (!siteId) {
      throw new Error('site_id is required');
    }
    if (tableIdentifier === null || tableIdentifier === undefined) {
      throw new Error('table_id or table_no is required');
    }

    // Build query parameters
    const params = new URLSearchParams();
    params.append('site_id', siteId.toString());
    
    // Check if tableIdentifier is a UUID (table_id) or a number (table_no)
    const isTableId = typeof tableIdentifier === 'string' && tableIdentifier.startsWith('tbl_');
    
    if (isTableId) {
      params.append('table_id', tableIdentifier);
    } else {
      // Using table_no, floor_no is required
      if (floorNo === null || floorNo === undefined) {
        // Try to get floor_no from updateData
        if (updateData.floor_no) {
          params.append('table_no', tableIdentifier.toString());
          params.append('floor_no', updateData.floor_no.toString());
        } else {
          throw new Error('floor_no is required when using table_no');
        }
      } else {
        params.append('table_no', tableIdentifier.toString());
        params.append('floor_no', floorNo.toString());
      }
    }
    
    const queryString = params.toString();
    const endpoint = `/tables/tables?${queryString}`;
    const response = await makeApiRequest(endpoint, 'PUT', updateData);
    const data = await response.json();
    
    // Log API event for successful update
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully updated table`,
      {
        endpoint: '/tables/tables',
        method: 'PUT',
        siteId,
        tableId: isTableId ? tableIdentifier : null,
        tableNo: !isTableId ? tableIdentifier : null,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /**
   * Update a table by table_id
   * PUT /tables/tables?site_id={site_id}&table_id={table_id}
   * @param {string} siteId - Site identifier (required)
   * @param {string} tableId - Table ID (tbl_uuid) (required)
   * @param {Object} updateData - Partial update data
   * @returns {Promise<Object>} Response with updated table
   */
  updateTable: async (siteId, tableId, updateData) => {
    if (!siteId) {
      throw new Error('site_id is required');
    }
    if (!tableId) {
      throw new Error('table_id is required');
    }
    
    const params = new URLSearchParams();
    params.append('site_id', siteId.toString());
    params.append('table_id', tableId.toString());
    
    const queryString = params.toString();
    const endpoint = `/tables/tables?${queryString}`;
    const response = await makeApiRequest(endpoint, 'PUT', updateData);
    const data = await response.json();
    
    // Log API event for successful update
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully updated table`,
      {
        endpoint: '/tables/tables',
        method: 'PUT',
        siteId,
        tableId,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /**
   * Delete a single table
   * DELETE /tables/tables?site_id={site_id}&table_id={table_id}
   * OR DELETE /tables/tables?site_id={site_id}&table_no={table_no}&floor_no={floor_no}
   * @param {string} siteId - Site identifier (required)
   * @param {string|number} tableIdentifier - Table ID (tbl_uuid) OR table number
   * @param {number} [floorNo] - Floor number (required if using table_no instead of table_id)
   * @returns {Promise<Object>} Response with deletion confirmation
   */
  deleteTable: async (siteId, tableIdentifier, floorNo = null) => {
    if (!siteId) {
      throw new Error('site_id is required');
    }
    if (tableIdentifier === null || tableIdentifier === undefined) {
      throw new Error('table_id or table_no is required');
    }

    const params = new URLSearchParams();
    params.append('site_id', siteId.toString());
    
    // Check if tableIdentifier is a UUID (table_id) or a number (table_no)
    const isTableId = typeof tableIdentifier === 'string' && tableIdentifier.startsWith('tbl_');
    
    if (isTableId) {
      params.append('table_id', tableIdentifier);
    } else {
      // Using table_no, floor_no is required
      if (floorNo === null || floorNo === undefined) {
        throw new Error('floor_no is required when using table_no');
      }
      params.append('table_no', tableIdentifier.toString());
      params.append('floor_no', floorNo.toString());
    }
    
    const queryString = params.toString();
    const endpoint = `/tables/tables?${queryString}`;
    const response = await makeApiRequest(endpoint, 'DELETE');
    const data = await response.json();
    
    // Log API event for successful deletion
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully deleted table`,
      {
        endpoint: '/tables/tables',
        method: 'DELETE',
        siteId,
        tableId: isTableId ? tableIdentifier : null,
        tableNo: !isTableId ? tableIdentifier : null,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /**
   * Delete a section (all tables in that section).
   * DELETE /tables/sections?site_id={site_id}&section={section}&floor_no={floor_no}
   * This operation is DESTRUCTIVE and permanently removes tables.
   * @param {string} siteId - Site identifier (required)
   * @param {string} section - Section name to delete, e.g. "Patio" (required)
   * @param {number} [floorNo] - If provided, only deletes tables in this section on this floor
   * @returns {Promise<Object>} { message, tables_deleted, errors }
   */
  deleteSection: async (siteId, section, floorNo = null) => {
    if (!siteId) throw new Error('site_id is required');
    if (!section || String(section).trim() === '') throw new Error('section is required');
    const params = new URLSearchParams();
    params.append('site_id', siteId.toString());
    params.append('section', String(section).trim());
    if (floorNo != null && floorNo !== '') {
      params.append('floor_no', String(floorNo));
    }
    const endpoint = `/tables/sections?${params.toString()}`;
    const response = await makeApiRequest(endpoint, 'DELETE');
    const data = await response.json();
    
    // Log API event for successful deletion
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully deleted section`,
      {
        endpoint: '/tables/sections',
        method: 'DELETE',
        siteId,
        section,
        floorNo,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  // ==================== Bookings ====================

  /**
   * Get bookings with filters (legacy - use getTableBookings instead)
   * GET /tables/bookings?site_id={siteId}&date={date}&status={status}
   * @deprecated Use getTableBookings for better date handling
   */
  getBookings: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    const endpoint = queryString ? `/tables/bookings?${queryString}` : '/tables/bookings';
    const response = await makeApiRequest(endpoint);
    const data = await response.json();
    
    // Log API event for successful fetch
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully fetched bookings`,
      {
        endpoint: '/tables/bookings',
        method: 'GET',
        siteId: filters.site_id,
        date: filters.date,
        status: filters.status,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /**
   * Get a specific booking
   * GET /tables/bookings/{booking_id}
   */
  getBooking: async (bookingId) => {
    const response = await makeApiRequest(`/tables/bookings/${bookingId}`);
    const data = await response.json();
    
    // Log API event for successful fetch
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully fetched booking`,
      {
        endpoint: `/tables/bookings/${bookingId}`,
        method: 'GET',
        bookingId,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /**
   * Update a booking
   * PUT /tables/bookings/{booking_id}
   */
  updateBooking: async (bookingId, bookingData) => {
    const response = await makeApiRequest(`/tables/bookings/${bookingId}`, 'PUT', bookingData);
    const data = await response.json();
    
    // Log API event for successful update
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully updated booking`,
      {
        endpoint: `/tables/bookings/${bookingId}`,
        method: 'PUT',
        bookingId,
        siteId: bookingData.site_id,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /**
   * Delete a booking
   * DELETE /tables/bookings/{booking_id}
   */
  deleteBooking: async (bookingId) => {
    const response = await makeApiRequest(`/tables/bookings/${bookingId}`, 'DELETE');
    const data = await response.json();
    
    // Log API event for successful deletion
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully deleted booking`,
      {
        endpoint: `/tables/bookings/${bookingId}`,
        method: 'DELETE',
        bookingId,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /**
   * Get no-show risk score for a booking
   * Note: This endpoint may not exist in the new API. Use getBooking() instead to get no_show_risk_score from booking data.
   * @deprecated Use getBooking() and extract no_show_risk_score from the booking object
   */
  getNoShowRiskScore: async (bookingId) => {
    // Try to get booking and extract risk score
    try {
      const booking = await tableBookingApi.getBooking(bookingId);
      return {
        no_show_risk_score: booking.no_show_risk_score || 0.15,
        booking_details: booking,
      };
    } catch (error) {
      // Fallback if endpoint doesn't exist
      console.warn('getNoShowRiskScore: Using booking data fallback');
      throw error;
    }
  },

  /**
   * Create a new table booking
   * POST /tables/bookings
   * @param {Object} bookingData - Booking data object containing:
   *   - site_id: string (required)
   *   - party_size: number
   *   - guest_name: string (optional)
   *   - guest_contact: string (e.g., "+91XXXXXXXXXX")
   *   - guest_email: string (optional)
   *   - notes: string (optional)
   *   - estimated_dining_duration: number (minutes)
   *   - no_show_risk_score: number (0-1, optional)
   *   - table_merging_allowed_flag: boolean (optional)
   *   - booking_start: string (ISO 8601 datetime, e.g., "2024-01-15T19:00:00Z")
   *   - booking_end: string (ISO 8601 datetime, e.g., "2024-01-15T21:00:00Z")
   *   - table_id: string (optional, tbl_uuid - replaces table_no/floor_no)
   *   - table_no: number (optional, use with floor_no)
   *   - floor_no: number (optional, use with table_no)
   *   - status: string ("booked"|"confirmed"|"seated"|"completed"|"no_show"|"cancelled", optional)
   * @returns {Promise<Object>} Response with booking_id: { message: "Booking created", booking_id: "bkg_uuid" }
   */
  createTableBooking: async (bookingData) => {
    if (!bookingData.site_id) {
      throw new Error('site_id is required');
    }
    const response = await makeApiRequest('/tables/bookings', 'POST', bookingData);
    const data = await response.json();
    
    // Log API event for successful creation
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully created table booking`,
      {
        endpoint: '/tables/bookings',
        method: 'POST',
        siteId: bookingData.site_id,
        bookingId: data.booking_id,
        partySize: bookingData.party_size,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /**
   * Delete a table booking
   * DELETE /tables/bookings/{booking_id}
   * @param {string} bookingId - Booking identifier (required)
   * @returns {Promise<Object>} Response with success message: { message: "Booking deleted successfully" }
   */
  deleteTableBooking: async (bookingId) => {
    if (!bookingId) {
      throw new Error('booking_id is required');
    }
    
    const endpoint = `/tables/bookings/${bookingId}`;
    const response = await makeApiRequest(endpoint, 'DELETE');
    const data = await response.json();
    
    // Log API event for successful deletion
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully deleted table booking`,
      {
        endpoint: `/tables/bookings/${bookingId}`,
        method: 'DELETE',
        bookingId,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /**
   * Get table bookings for a site, optionally filtered by date and section.
   * GET /tables/bookings?site_id={site_id}&date={YYYY-MM-DD}&section={section_name}
   * @param {string} siteId - Site identifier (required)
   * @param {string} [date] - Optional. Filter by date YYYY-MM-DD (matches booking_start)
   * @param {string} [section] - Optional. Filter by section name (e.g. "Patio"). Requires site_id.
   * @returns {Promise<Array>} Array of bookings: [{ booking_id, tenant_id, site_id, party_size, guest_name, guest_contact, guest_email, notes, booking_start, booking_end, table_id, table_no, floor_no, status, ... }]
   */
  getTableBookings: async (siteId, date, section) => {
    if (!siteId) {
      throw new Error('site_id is required');
    }

    const params = new URLSearchParams();
    params.append('site_id', siteId);
    if (date) {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(date)) {
        throw new Error('date must be in YYYY-MM-DD format');
      }
      params.append('date', date);
    }
    if (section != null && section !== '') {
      params.append('section', section);
    }

    const endpoint = `/tables/bookings?${params.toString()}`;
    const response = await makeApiRequest(endpoint);
    const data = await response.json();

    const bookings = (data && Array.isArray(data.bookings)) ? data.bookings : 
                     (Array.isArray(data) ? data : 
                     (data && data.bookings ? (Array.isArray(data.bookings) ? data.bookings : []) : []));

    // Log API event for successful fetch
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully fetched table bookings`,
      {
        endpoint: '/tables/bookings',
        method: 'GET',
        siteId,
        date,
        section,
        bookingsCount: bookings.length,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });

    if (bookings.length > 0 || (data && Array.isArray(data.bookings))) {
      return bookings;
    }
    if (Array.isArray(data)) {
      return data;
    }
    if (data && data.bookings) {
      return Array.isArray(data.bookings) ? data.bookings : [];
    }

    console.warn('Unexpected API response format for getTableBookings:', data);
    return [];
  },

  // ==================== Waitlist ====================

  /**
   * List waitlist entries for a site
   * GET /tables/waitlist?site_id={site_id}
   * @param {Object} filters - Query parameters: { site_id (required) }
   * @returns {Promise<Array>} Array of waitlist entries
   */
  getWaitlist: async (filters = {}) => {
    if (!filters.site_id) {
      throw new Error('site_id is required');
    }
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });
    const queryString = params.toString();
    const endpoint = `/tables/waitlist?${queryString}`;
    const response = await makeApiRequest(endpoint);
    const data = await response.json();
    const waitlist = Array.isArray(data) ? data : (data && Array.isArray(data.waitlist) ? data.waitlist : []);
    
    // Log API event for successful fetch
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully fetched waitlist`,
      {
        endpoint: '/tables/waitlist',
        method: 'GET',
        siteId: filters.site_id,
        waitlistCount: waitlist.length,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return waitlist;
  },

  /**
   * Get a single waitlist entry
   * GET /tables/waitlist/{waitlist_id}
   * @param {string} waitlistId - Waitlist ID (wtl_uuid)
   * @returns {Promise<Object>} Waitlist entry object
   */
  getWaitlistEntry: async (waitlistId) => {
    if (!waitlistId) throw new Error('waitlist_id is required');
    const response = await makeApiRequest(`/tables/waitlist/${waitlistId}`);
    const data = await response.json();
    
    // Log API event for successful fetch
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully fetched waitlist entry`,
      {
        endpoint: `/tables/waitlist/${waitlistId}`,
        method: 'GET',
        waitlistId,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /**
   * Create waitlist entry
   * POST /tables/waitlist
   * Response: 201 Created
   * @param {Object} waitlistData - Request body:
   *   - site_id: string (required)
   *   - party_size: number (optional, default 2)
   *   - guest_name: string (optional)
   *   - guest_contact: string e.g. "+91XXXXXXXXXX" (optional)
   *   - eta_minutes: number (optional)
   *   - notify_status: "pending"|"notified"|"seated"|"left" (optional)
   *   - notes: string (optional)
   * @returns {Promise<Object>} { message: "Waitlist entry created", waitlist_id: "wtl_uuid" }
   */
  createWaitlistEntry: async (waitlistData) => {
    if (!waitlistData || !waitlistData.site_id) {
      throw new Error('site_id is required');
    }
    const response = await makeApiRequest('/tables/waitlist', 'POST', waitlistData);
    const data = await response.json();
    
    // Log API event for successful creation
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully created waitlist entry`,
      {
        endpoint: '/tables/waitlist',
        method: 'POST',
        siteId: waitlistData.site_id,
        waitlistId: data.waitlist_id,
        partySize: waitlistData.party_size,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /**
   * Update waitlist entry
   * PUT /tables/waitlist/{waitlist_id}
   * @param {string} waitlistId - Waitlist ID (wtl_uuid)
   * @param {Object} waitlistData - Partial update: party_size, guest_name, guest_contact, eta_minutes, notify_status, notes
   * @returns {Promise<Object>} Updated waitlist entry or API response
   */
  updateWaitlistEntry: async (waitlistId, waitlistData) => {
    if (!waitlistId) throw new Error('waitlist_id is required');
    const response = await makeApiRequest(`/tables/waitlist/${waitlistId}`, 'PUT', waitlistData);
    const data = await response.json();
    
    // Log API event for successful update
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully updated waitlist entry`,
      {
        endpoint: `/tables/waitlist/${waitlistId}`,
        method: 'PUT',
        waitlistId,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /**
   * Delete waitlist entry
   * DELETE /tables/waitlist/{waitlist_id}
   * @param {string} waitlistId - Waitlist ID (wtl_uuid)
   * @returns {Promise<Object>} API response
   */
  deleteWaitlistEntry: async (waitlistId) => {
    if (!waitlistId) throw new Error('waitlist_id is required');
    const response = await makeApiRequest(`/tables/waitlist/${waitlistId}`, 'DELETE');
    const data = await response.json();
    
    // Log API event for successful deletion
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully deleted waitlist entry`,
      {
        endpoint: `/tables/waitlist/${waitlistId}`,
        method: 'DELETE',
        waitlistId,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  /** @deprecated Use updateWaitlistEntry instead */
  updateWaitlist: async (waitlistId, waitlistData) => {
    return tableBookingApi.updateWaitlistEntry(waitlistId, waitlistData);
  },

  /** @deprecated Use deleteWaitlistEntry instead */
  removeFromWaitlist: async (waitlistId) => {
    return tableBookingApi.deleteWaitlistEntry(waitlistId);
  },

  /**
   * Add party to waitlist (legacy alias - kept for backward compatibility)
   * @deprecated Use createWaitlistEntry instead
   */
  addToWaitlist: async (waitlistData) => {
    return tableBookingApi.createWaitlistEntry(waitlistData);
  },

  /**
   * Notify waitlist party (legacy - may not be supported in new API)
   * Note: This endpoint may not exist. Use updateWaitlist to change notify_status instead.
   * @deprecated Use updateWaitlist with notify_status instead
   */
  notifyWaitlistParty: async (waitlistId) => {
    // Try to update notify_status to 'notified'
    return tableBookingApi.updateWaitlist(waitlistId, { notify_status: 'notified' });
  },

  /**
   * Seat waitlist party (legacy - may not be supported in new API)
   * Note: This endpoint may not exist. Create a booking instead.
   * @deprecated Create a booking directly instead
   */
  seatWaitlistParty: async (waitlistId, tableId) => {
    console.warn('seatWaitlistParty is deprecated. Create a booking directly instead.');
    // This endpoint may not exist in new API
    // Consider creating a booking and then deleting the waitlist entry
    throw new Error('seatWaitlistParty is deprecated. Create a booking directly instead.');
  },

  /**
   * Delay waitlist party ETA (legacy - may not be supported in new API)
   * Note: This endpoint may not exist. Use updateWaitlist to change eta_minutes instead.
   * @deprecated Use updateWaitlist with updated eta_minutes instead
   */
  delayWaitlistParty: async (waitlistId, minutes = 10) => {
    // Get current waitlist entry to calculate new eta_minutes
    const entry = await tableBookingApi.getWaitlistEntry(waitlistId);
    const currentEta = entry.eta_minutes || 0;
    const newEta = currentEta + minutes;
    return tableBookingApi.updateWaitlist(waitlistId, { eta_minutes: newEta });
  },

  // ==================== Status Events ====================

  /**
   * Create table status event
   * POST /tables/status-events
   * @param {Object} eventData - Event data: { site_id (required), table_no (required, integer), status (required), details (optional) }
   * @returns {Promise<Object>} Response: { message: "Status event created", ... }
   */
  createStatusEvent: async (eventData) => {
    if (!eventData.site_id) {
      throw new Error('site_id is required');
    }
    if (eventData.table_no === null || eventData.table_no === undefined) {
      throw new Error('table_no is required');
    }
    if (!eventData.status) {
      throw new Error('status is required');
    }
    const response = await makeApiRequest('/tables/status-events', 'POST', eventData);
    const data = await response.json();
    
    // Log API event for successful creation
    loggingService.logInfo(
      'api_success_table_booking',
      `Successfully created status event`,
      {
        endpoint: '/tables/status-events',
        method: 'POST',
        siteId: eventData.site_id,
        tableNo: eventData.table_no,
        status: eventData.status,
      }
    ).catch(logError => {
      console.warn('[tableBookingApi] Failed to log success event:', logError);
    });
    
    return data;
  },

  // ==================== GraphQL Subscriptions (AppSync) ====================

  /**
   * Subscribe to table status changes
   * This will use AppSync GraphQL subscriptions when configured
   */
  subscribeToTableStatusChanges: (siteId, onUpdate) => {
    if (!APPSYNC_GRAPHQL_ENDPOINT) {
      console.warn('AppSync not configured. Using polling fallback.');
      // Fallback to polling if AppSync not available
      const pollInterval = setInterval(async () => {
        try {
          const response = await tableBookingApi.getTablesWithFilters({ site_id: siteId });
          const tables = response?.tables || response || [];
          onUpdate(tables);
        } catch (error) {
          console.error('Error polling table status:', error);
        }
      }, 5000); // Poll every 5 seconds

      return () => clearInterval(pollInterval);
    }

    // GraphQL subscription query
    const subscriptionQuery = `
      subscription OnTableStatusChanged($siteId: ID!) {
        onTableStatusChanged(site_id: $siteId) {
          table_id
          status
          booking_id
          updated_at
        }
      }
    `;

    // TODO: Implement AppSync subscription using AWS Amplify GraphQL client
    // This requires AppSync configuration and @aws-amplify/api-graphql package
    console.warn('AppSync subscription not yet implemented. Using polling fallback.');
    
    // Fallback to polling
    const pollInterval = setInterval(async () => {
      try {
        const response = await tableBookingApi.getTablesWithFilters({ site_id: siteId });
        const tables = response?.tables || response || [];
        onUpdate(tables);
      } catch (error) {
        console.error('Error polling table status:', error);
      }
    }, 5000);

    return () => clearInterval(pollInterval);
  },
};

export default tableBookingApi;
